import React, { useState } from "react";

// Category → premium fallback image (verified working Unsplash IDs).
// Used when the primary image URL is missing, empty, or fails to load.
const FALLBACK_BY_MOMENT = {
  photographer: "https://images.unsplash.com/photo-1452587925148-ce544e77e70d?w=1200", // photographer with camera
  "private-chef": "https://images.unsplash.com/photo-1559339352-11d035aa65de?w=1200",  // chef plating
  chef:            "https://images.unsplash.com/photo-1559339352-11d035aa65de?w=1200",
  wellness:        "https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=1200", // spa
  proposal:        "https://images.unsplash.com/photo-1519741497674-611481863552?w=1200", // romantic setup
  birthday:        "https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=1200", // celebration
  romantic:        "https://images.unsplash.com/photo-1536392706976-e486e2ba97af?w=1200",
  vip:             "https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=1200", // luxury car
  transportation:  "https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=1200",
  family:          "https://images.unsplash.com/photo-1602002418082-a4443e081dd1?w=1200",
  "tour-guide":    "https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1200",
  "event-planner": "https://images.unsplash.com/photo-1519741497674-611481863552?w=1200",
  decorator:       "https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=1200",
  concierge:       "https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9?w=1200",
  luxury:          "https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9?w=1200",
  date:            "https://images.unsplash.com/photo-1536392706976-e486e2ba97af?w=1200",
  girls:           "https://images.unsplash.com/photo-1529333166437-7750a6dd5a70?w=1200",
  guys:            "https://images.unsplash.com/photo-1543007630-9710e4a00a20?w=1200",
  hidden:          "https://images.unsplash.com/photo-1483728642387-6c3bdd6c93e5?w=1200",
  adventure:       "https://images.unsplash.com/photo-1551632811-561732d1e306?w=1200",
  bucket:          "https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1200",
};
// Last-resort default — luxury hospitality scene
const GENERIC_FALLBACK = "https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9?w=1200";

function pickFallback(exp = {}) {
  const candidates = [
    exp.moment, exp.category, exp.service_type, exp.tag,
    (exp.title || "").toLowerCase().includes("photograph") && "photographer",
    (exp.title || "").toLowerCase().includes("chef") && "private-chef",
    (exp.title || "").toLowerCase().includes("wellness") && "wellness",
    (exp.title || "").toLowerCase().includes("proposal") && "proposal",
    (exp.title || "").toLowerCase().includes("birthday") && "birthday",
    (exp.title || "").toLowerCase().includes("airport") && "vip",
    (exp.title || "").toLowerCase().includes("family") && "family",
  ].filter(Boolean);
  for (const c of candidates) {
    if (FALLBACK_BY_MOMENT[c]) return FALLBACK_BY_MOMENT[c];
  }
  return GENERIC_FALLBACK;
}

/**
 * SafeExperienceImg — never shows a broken-image icon.
 * - Uses primary src if valid
 * - On load error OR empty src, swaps to category fallback
 * - On second failure, swaps to generic luxury fallback
 * Preserves rounded corners, object-cover, sizing — purely behavioral, not visual.
 */
export default function SafeExperienceImg({ exp, alt = "", className = "", style }) {
  const primary = exp?.images?.[0];
  const fallback = pickFallback(exp);
  const [src, setSrc] = useState(primary && primary.trim() ? primary : fallback);
  const [stage, setStage] = useState(primary && primary.trim() ? "primary" : "fallback");
  const onError = () => {
    if (stage === "primary") { setSrc(fallback); setStage("fallback"); }
    else if (stage === "fallback") { setSrc(GENERIC_FALLBACK); setStage("generic"); }
  };
  return (
    <img
      src={src}
      alt={alt || exp?.title || "TREL experience"}
      className={className}
      style={style}
      onError={onError}
      loading="lazy"
      draggable={false}
    />
  );
}

export { pickFallback, FALLBACK_BY_MOMENT, GENERIC_FALLBACK };
